/**
 * Background Tasks — Zustand Store
 *
 * Singleton store for all project background tasks.
 * Maintains a single Firestore collection listener per project.
 * Provides filtered selectors and task actions.
 *
 * Usage:
 *   const tasks = useTaskStore(s => s.getFilteredTasks({ statusMode: 'active' }));
 *   const { cancelTask, markViewed } = useTaskStore(s => s.actions);
 */

import { create } from 'zustand';
import { TaskFirestoreService } from '../services/TaskFirestoreService';
import type {
  BackendTask,
  TaskStatus,
  TaskFilter,
  StatusFilterMode,
  OwnerFilterMode,
} from '../types';
import { ACTIVE_STATUSES, TERMINAL_STATUSES } from '../types';

// ── Singleton service instance ──
const firestoreService = new TaskFirestoreService();

// ── Helper: apply filters to task list ──
function applyFilter(
  tasks: BackendTask[],
  filter: TaskFilter | undefined,
  currentUserId: string | null
): BackendTask[] {
  if (!filter) return tasks;

  let result = tasks;

  // Status mode (convenience)
  if (filter.statusMode) {
    const statusSet =
      filter.statusMode === 'active'
        ? ACTIVE_STATUSES
        : filter.statusMode === 'completed'
          ? TERMINAL_STATUSES
          : null; // 'all' = no filter
    if (statusSet) {
      result = result.filter((t) => statusSet.includes(t.status));
    }
  }

  // Explicit status filter (overrides statusMode if both provided)
  if (filter.status) {
    const statuses = Array.isArray(filter.status) ? filter.status : [filter.status];
    result = result.filter((t) => statuses.includes(t.status));
  }

  // Task type
  if (filter.taskType) {
    const types = Array.isArray(filter.taskType) ? filter.taskType : [filter.taskType];
    result = result.filter((t) => t.taskType && types.includes(t.taskType));
  }

  // Entity scope
  if (filter.strategyId) {
    result = result.filter((t) => t.strategyId === filter.strategyId);
  }
  if (filter.authorId) {
    result = result.filter((t) => (t as unknown as { authorId?: string }).authorId === filter.authorId);
  }
  if (filter.blockId) {
    result = result.filter((t) => t.blockId === filter.blockId);
  }
  if (filter.screenId) {
    // screenId is not on the typed BackendTask but image-generation tasks write
    // it at the top level of the Firestore doc. Runtime cast reads it safely.
    result = result.filter((t) => (t as unknown as { screenId?: string }).screenId === filter.screenId);
  }

  // Owner filter
  if (filter.ownerMode === 'mine' && currentUserId) {
    result = result.filter((t) => t.userId === currentUserId);
  }
  if (filter.userId) {
    result = result.filter((t) => t.userId === filter.userId);
  }

  // Unviewed only
  if (filter.unviewedOnly && currentUserId) {
    result = result.filter(
      (t) => !t.viewedBy?.includes(currentUserId)
    );
  }

  // Sorting
  const sortBy = filter.sortBy ?? 'createdAt';
  const dir = filter.sortDirection === 'asc' ? 1 : -1;
  result.sort((a, b) => {
    const aVal = a[sortBy] instanceof Date ? (a[sortBy] as Date).getTime() : 0;
    const bVal = b[sortBy] instanceof Date ? (b[sortBy] as Date).getTime() : 0;
    return (aVal - bVal) * dir;
  });

  return result;
}

// ── Deletion result surfaced to the UI ──

/**
 * What `deleteTasks` actually achieved. The UI must be able to tell the user that
 * some tasks were refused (active ones) rather than pretend the whole batch went
 * through — the module's old `removeTask` dropped rows locally and the collection
 * listener silently put them back.
 */
export interface TaskDeletionOutcome {
  deleted: string[];
  /** Refused server-side: still running, or already gone. */
  skipped: { taskId: string; reason: 'active' | 'not_found' }[];
  failed: { taskId: string; error: string }[];
  /** Set when the request itself failed (network / auth / 5xx). Nothing was deleted. */
  error?: string;
}

// ── Store interface ──

interface TaskStoreState {
  // Data
  tasks: Record<string, BackendTask>;
  projectId: string | null;
  currentUserId: string | null;
  isConnected: boolean;

  // UI filter state (persists across component unmounts)
  statusFilter: StatusFilterMode;
  ownerFilter: OwnerFilterMode;

  // Actions
  connect: (projectId: string, userId: string) => void;
  disconnect: () => void;
  cancelTask: (taskId: string) => Promise<void>;
  removeTask: (taskId: string) => void;
  deleteTasks: (taskIds: string[]) => Promise<TaskDeletionOutcome>;
  upsertTask: (task: BackendTask) => void;
  refreshTask: (taskId: string) => Promise<void>;
  markViewed: (taskId: string) => Promise<void>;
  setStatusFilter: (mode: StatusFilterMode) => void;
  setOwnerFilter: (mode: OwnerFilterMode) => void;

  // Selectors (computed from state)
  getFilteredTasks: (filter?: TaskFilter) => BackendTask[];
  getRunningTasks: () => BackendTask[];
  getCompletedTasks: () => BackendTask[];
  getUnviewedCount: (scopeFilter?: Pick<TaskFilter, 'strategyId' | 'blockId'>) => number;
  getTasksForEntity: (strategyId?: string, blockId?: string) => BackendTask[];
}

// ── Internal: collection listener unsubscribe ──
let _unsubscribeCollection: (() => void) | null = null;

export const useTaskStore = create<TaskStoreState>((set, get) => ({
  // Initial state
  tasks: {},
  projectId: null,
  currentUserId: null,
  isConnected: false,
  statusFilter: 'active',
  ownerFilter: 'mine',

  // ── Actions ──

  connect: (projectId: string, userId: string) => {
    const state = get();

    // Already connected to the same project
    if (state.isConnected && state.projectId === projectId) return;

    // Disconnect previous if different project
    if (_unsubscribeCollection) {
      _unsubscribeCollection();
      _unsubscribeCollection = null;
    }

    set({ projectId, currentUserId: userId, isConnected: true, tasks: {} });

    // Collection listener delivering per-doc changes only: unchanged tasks keep
    // their previous object identity, so useTask(id) subscribers of untouched
    // tasks never re-render when an unrelated task heartbeats.
    _unsubscribeCollection = firestoreService.subscribeToCollectionChanges(
      projectId,
      (changes) => {
        set((state) => {
          const nextTasks: Record<string, BackendTask> = { ...state.tasks };
          for (const change of changes) {
            if (change.type === 'removed') {
              delete nextTasks[change.task.id];
            } else {
              nextTasks[change.task.id] = change.task;
            }
          }
          return { tasks: nextTasks };
        });
      },
      (error) => {
        console.error('[TaskStore] Collection listener error:', error);
      }
    );
  },

  disconnect: () => {
    if (_unsubscribeCollection) {
      _unsubscribeCollection();
      _unsubscribeCollection = null;
    }
    set({
      tasks: {},
      projectId: null,
      currentUserId: null,
      isConnected: false,
    });
  },

  cancelTask: async (taskId: string) => {
    const { projectId, tasks } = get();
    if (!projectId) return;

    const task = tasks[taskId];
    if (!task) return;

    // Optimistic update
    set({
      tasks: {
        ...tasks,
        [taskId]: { ...task, status: 'cancelling' as TaskStatus },
      },
    });

    try {
      // This module is loaded dynamically because it reads Vite's import.meta
      // environment and must stay outside the CJS Jest import graph.
      const { cancelTaskOnServer } = await import('../services/TaskDeleteService');
      await cancelTaskOnServer(projectId, taskId);
    } catch (error) {
      // Revert optimistic update on failure
      set({
        tasks: {
          ...get().tasks,
          [taskId]: { ...task, status: task.status },
        },
      });
      console.error('[TaskStore] Cancel failed:', error);
    }
  },

  removeTask: (taskId: string) => {
    const { tasks } = get();
    const { [taskId]: _removed, ...remaining } = tasks;
    set({ tasks: remaining });
  },

  /**
   * Permanently deletes tasks server-side (doc + ai_calls + payload_chunks).
   *
   * Unlike `removeTask`, which only drops the entry from this map and is undone by
   * the next collection snapshot, this removes the Firestore documents for real.
   * Local state is pruned only for the ids the server confirms — the listener's
   * 'removed' change would do it anyway, but pruning here makes the UI immediate.
   *
   * The service is loaded dynamically: it reads `import.meta.env`, which ts-jest
   * (CJS) cannot parse, so it must stay out of this module's static import graph.
   */
  deleteTasks: async (taskIds: string[]): Promise<TaskDeletionOutcome> => {
    const { projectId } = get();
    if (!projectId || taskIds.length === 0) {
      return { deleted: [], skipped: [], failed: [] };
    }

    try {
      const { deleteTasksOnServer } = await import('../services/TaskDeleteService');
      const result = await deleteTasksOnServer(projectId, taskIds);

      if (result.deletedIds.length > 0) {
        const remaining = { ...get().tasks };
        for (const id of result.deletedIds) delete remaining[id];
        set({ tasks: remaining });
      }

      return {
        deleted: result.deletedIds,
        skipped: result.skipped ?? [],
        failed: result.failed ?? [],
      };
    } catch (error) {
      console.error('[TaskStore] deleteTasks failed:', error);
      return {
        deleted: [],
        skipped: [],
        failed: [],
        error: (error as Error).message,
      };
    }
  },

  upsertTask: (task: BackendTask) => {
    set((state) => ({
      tasks: {
        ...state.tasks,
        [task.id]: task,
      },
    }));
  },

  refreshTask: async (taskId: string) => {
    const { projectId } = get();
    if (!projectId) return;
    try {
      const task = await firestoreService.get(projectId, taskId);
      if (!task) return;
      set((state) => ({
        tasks: {
          ...state.tasks,
          [task.id]: task,
        },
      }));
    } catch (error) {
      console.error('[TaskStore] refreshTask failed:', error);
    }
  },

  markViewed: async (taskId: string) => {
    const { projectId, currentUserId, tasks } = get();
    if (!projectId || !currentUserId) return;

    const task = tasks[taskId];
    if (!task) return;

    // Already viewed
    if (task.viewedBy?.includes(currentUserId)) return;

    // Optimistic update
    set({
      tasks: {
        ...tasks,
        [taskId]: {
          ...task,
          viewedBy: [...(task.viewedBy ?? []), currentUserId],
        },
      },
    });

    try {
      await firestoreService.markViewed(projectId, taskId, currentUserId);
    } catch (error) {
      console.error('[TaskStore] markViewed failed:', error);
    }
  },

  setStatusFilter: (mode: StatusFilterMode) => set({ statusFilter: mode }),
  setOwnerFilter: (mode: OwnerFilterMode) => set({ ownerFilter: mode }),

  // ── Selectors ──

  getFilteredTasks: (filter?: TaskFilter) => {
    const state = get();
    const allTasks = Object.values(state.tasks);
    return applyFilter(allTasks, filter, state.currentUserId);
  },

  getRunningTasks: () => {
    const state = get();
    return Object.values(state.tasks).filter((t) =>
      ACTIVE_STATUSES.includes(t.status)
    );
  },

  getCompletedTasks: () => {
    const state = get();
    return Object.values(state.tasks).filter((t) =>
      TERMINAL_STATUSES.includes(t.status)
    );
  },

  getUnviewedCount: (scopeFilter?) => {
    const state = get();
    const userId = state.currentUserId;
    if (!userId) return 0;

    let tasks = Object.values(state.tasks).filter(
      (t) => TERMINAL_STATUSES.includes(t.status) && !t.viewedBy?.includes(userId)
    );

    if (scopeFilter?.strategyId) {
      tasks = tasks.filter((t) => t.strategyId === scopeFilter.strategyId);
    }
    if (scopeFilter?.blockId) {
      tasks = tasks.filter((t) => t.blockId === scopeFilter.blockId);
    }

    return tasks.length;
  },

  getTasksForEntity: (strategyId?: string, blockId?: string) => {
    const state = get();
    let tasks = Object.values(state.tasks);

    if (strategyId) {
      tasks = tasks.filter((t) => t.strategyId === strategyId);
    }
    if (blockId) {
      tasks = tasks.filter((t) => t.blockId === blockId);
    }

    return tasks.sort(
      (a, b) =>
        (b.createdAt instanceof Date ? b.createdAt.getTime() : 0) -
        (a.createdAt instanceof Date ? a.createdAt.getTime() : 0)
    );
  },
}));

/** Direct access to store state outside React (for services, tests) */
export const getTaskStoreState = useTaskStore.getState;

/** Firestore service instance (for direct usage in orchestrators) */
export const taskFirestoreService = firestoreService;
